/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { AirmetPhenomena, SigmetPhenomena } from '@opengeoweb/api';
import { dateUtils } from '@opengeoweb/shared';
import type {
  Warning,
  PublicWarningDetail,
} from '../../store/warningsForm/types';

export enum Domain {
  aviation = 'aviation',
  maritime = 'maritime',
  public = 'public',
}
export enum Level {
  extreme = 'extreme',
  moderate = 'moderate',
  severe = 'severe',
  airmet = 'AIR',
  sigmet = 'SIG',
}
export enum Phenomenon {
  wind = 'wind',
  fog = 'fog',
  thunderstorm = 'thunderstorm',
  snowIce = 'snowIce',
  rain = 'rain',
  highTemp = 'highTemp',
  lowTemp = 'lowTemp',
  coastalEvent = 'coastalEvent',
}

export const emptyFilterId = 'unspecified';

export const defaultDomainFilter: Record<Domain, boolean> = {
  aviation: false,
  maritime: false,
  public: true,
};

export const domainLevels: Record<Domain, Level[]> = {
  aviation: [Level.airmet, Level.sigmet],
  maritime: [Level.extreme, Level.moderate, Level.severe],
  public: [Level.extreme, Level.moderate, Level.severe],
};
export const defaultLevelFilter: Record<Level | 'unspecified', boolean> = {
  [emptyFilterId]: true,
  extreme: true,
  moderate: true,
  severe: true,
  AIR: true,
  SIG: true,
};

export const defaultEmptyFilter: Record<string, boolean> = {
  [emptyFilterId]: true,
};

export const makeFilterFromList = <T extends string>(
  options: T[],
  value = true,
): Record<T | 'unspecified', boolean> =>
  options.reduce(
    (acc, property) => {
      return property ? { ...acc, [property]: value } : acc;
    },
    { [emptyFilterId]: value } as Record<T | 'unspecified', boolean>,
  );

const AllPhenomena = {
  ...AirmetPhenomena,
  ...SigmetPhenomena,
  ...Phenomenon,
} as const;

export const AllPhenomenaKeys = Object.fromEntries(
  Object.keys(AllPhenomena).map((key) => [key, key]),
) as Record<keyof typeof AllPhenomena, keyof typeof AllPhenomena>;

export const domainPhenomena: Record<
  Domain,
  Partial<keyof typeof AllPhenomenaKeys>[]
> = {
  aviation: [
    ...Object.keys(SigmetPhenomena),
    ...Object.keys(AirmetPhenomena),
  ] as (keyof typeof AllPhenomenaKeys)[],
  maritime: [Phenomenon.wind],
  public: [...Object.keys(Phenomenon)] as (keyof typeof AllPhenomenaKeys)[],
};

export const defaultPhenomenonFilter = makeFilterFromList(
  Object.keys(AllPhenomenaKeys),
);

type OptionsType = Partial<Record<Domain | Level | Phenomenon, boolean>>;

interface OptionsMetadata {
  /** The number of selected options */
  noSelectedOptions: number;
  /** Whether or not all options are selected */
  allSelected: boolean;
}
export const getSelectedOptions = (
  options: OptionsType = {},
): OptionsMetadata => {
  const selectedOptions = Object.keys(options).filter(
    (key) => options[key as keyof typeof options],
  );

  const allSelected = selectedOptions.length === Object.keys(options).length;
  return { noSelectedOptions: selectedOptions.length, allSelected };
};

export const updateState = <T extends string>(
  state: Record<T, boolean>,
  optionKey: T | 'ALL',
  value: boolean,
  only?: boolean,
): Record<T, boolean> => {
  if (only) {
    return Object.keys(state).reduce(
      (acc, key) => ({ ...acc, [key]: key === optionKey }),
      {} as Record<T, boolean>,
    );
  }
  if (optionKey === 'ALL') {
    if (Object.keys(state).length < 2) {
      return state as Record<T, boolean>;
    }
    return Object.keys(state).reduce(
      (acc, key) => ({ ...acc, [key]: value }),
      {} as Record<T, boolean>,
    );
  }

  return { ...state, [optionKey]: value };
};

/** Make sure our value is in the filter */
export const getValue = <T>(
  values: Record<string, T>,
  value: string,
): T | 'unspecified' =>
  Object.values(values).find((existingValue) => existingValue === value) ??
  emptyFilterId;

export const getFiltersFromWarnings = (
  warnings: Warning[],
  filterKey: string,
): string[] => {
  const filterOptions: string[] = [emptyFilterId];
  if (!warnings?.length) {
    // return default list
    return filterOptions;
  }

  warnings.forEach((warning) => {
    const id = warning.warningDetail[
      filterKey as keyof PublicWarningDetail
    ] as string;
    if (id && filterOptions.findIndex((option) => option === id) === -1) {
      filterOptions.push(id);
    }
  });

  return filterOptions;
};

export enum TimeFilterMode {
  all = 'all',
  today = 'today',
  tomorrow = 'tomorrow',
  custom = 'custom',
}

export interface TimeFilter {
  mode: TimeFilterMode;
  customStart?: string;
  customEnd?: string;
}

export const defaultTimeFilter: TimeFilter = {
  mode: TimeFilterMode.all,
};

export const isWarningWithinInterval = (
  warning: Warning,
  timeFilter: TimeFilter,
): boolean => {
  // "NO FILTER"
  if (timeFilter.mode === TimeFilterMode.all) {
    return true;
  }

  const warningStart = new Date(warning.warningDetail.validFrom);
  const warningEnd = new Date(warning.warningDetail.validUntil);

  // CUSTOM TIME SPAN
  if (timeFilter.mode === TimeFilterMode.custom) {
    if (!timeFilter.customStart || !timeFilter.customEnd) {
      return true;
    }

    const filterStart = new Date(timeFilter.customStart);
    const filterEnd = new Date(timeFilter.customEnd);

    return warningEnd >= filterStart && warningStart <= filterEnd;
  }

  // TODAY or TOMORROW
  const now = new Date();
  let rangeStart: Date;
  let rangeEnd: Date;

  if (timeFilter.mode === TimeFilterMode.today) {
    // TODAY
    rangeStart = dateUtils.startOfDayUTC(now);
    rangeEnd = dateUtils.set(rangeStart, {
      date: rangeStart.getUTCDate() + 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
  } else {
    // TOMORROW
    rangeStart = dateUtils.set(now, {
      date: now.getUTCDate() + 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
    rangeEnd = dateUtils.set(rangeStart, {
      date: rangeStart.getUTCDate() + 1,
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
    });
  }
  return warningEnd >= rangeStart && warningStart < rangeEnd;
};

export const getApiParams = (
  timeFilter: TimeFilter,
): Record<string, string> => {
  if (timeFilter.mode === TimeFilterMode.all) {
    return {};
  }

  if (timeFilter.mode === TimeFilterMode.custom) {
    if (!timeFilter.customStart || !timeFilter.customEnd) {
      return {};
    }
    return {
      start_time: new Date(timeFilter.customStart).toISOString(),
      end_time: new Date(timeFilter.customEnd).toISOString(),
    };
  }
  return {};
};

export const initialFilters = {
  source: defaultEmptyFilter,
  domain: defaultDomainFilter,
  phenomenon: defaultPhenomenonFilter,
  level: defaultLevelFilter,
  time: defaultTimeFilter,
};
